// features/settings/pages/ReceiptTemplatesPage.tsx
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { useToastStore } from '@/store/toastStore';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { SettingsModal } from '../components/SettingsModal';
import * as api from '../services/settingsApi';
import {
    FileText, Plus, Save, ArrowLeft, Star, Printer, ScrollText,
    Image, Barcode, Pencil, Trash2,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { clsx } from 'clsx';

interface ReceiptTemplate {
    id: string;
    name: string;
    type: 'receipt' | 'invoice' | 'kitchen_order';
    headerText: string | null;
    footerText: string | null;
    showLogo: boolean;
    showTaxDetails: boolean;
    showBarcode: boolean;
    fontSize: string;
    paperSize: string;
    isDefault: boolean;
    isActive: boolean;
}

const TYPE_META: Record<string, { icon: React.ElementType; label: string; gradient: string; desc: string }> = {
    receipt: { icon: FileText, label: 'Sales Receipt', gradient: 'from-emerald-500 to-teal-600', desc: 'Customer receipt for sales transactions' },
    invoice: { icon: ScrollText, label: 'Invoice', gradient: 'from-blue-500 to-indigo-600', desc: 'Formal invoice for business customers' },
    kitchen_order: { icon: Printer, label: 'Kitchen Order', gradient: 'from-amber-500 to-orange-600', desc: 'Order slip for kitchen display' },
};

export function ReceiptTemplatesPage() {
    const navigate = useNavigate();
    const user = useAuthStore((s) => s.user);
    const queryClient = useQueryClient();
    const addToast = useToastStore((s) => s.addToast);
    const [showForm, setShowForm] = useState(false);
    const [editingTemplate, setEditingTemplate] = useState<ReceiptTemplate | null>(null);
    const [deleteConfirm, setDeleteConfirm] = useState<ReceiptTemplate | null>(null);

    const [form, setForm] = useState<{
        name: string;
        type: ReceiptTemplate['type'];
        headerText: string;
        footerText: string;
        showLogo: boolean;
        showTaxDetails: boolean;
        showBarcode: boolean;
        fontSize: string;
        paperSize: string;
        isDefault: boolean;
    }>({
        name: '', type: 'receipt', headerText: '', footerText: '',
        showLogo: true, showTaxDetails: true, showBarcode: false,
        fontSize: 'normal', paperSize: '80mm', isDefault: false,
    });

    const { data: templates, isLoading } = useQuery<ReceiptTemplate[]>({
        queryKey: ['receipt-templates', user?.tenantId],
        queryFn: () => api.getReceiptTemplates(user!.tenantId),
        enabled: !!user?.tenantId,
    });

    const saveMutation = useMutation({
        mutationFn: (payload: any) => editingTemplate
            ? api.updateReceiptTemplate(user!.tenantId, editingTemplate.id, payload)
            : api.createReceiptTemplate(user!.tenantId, payload),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['receipt-templates'] });
            addToast({ type: 'success', title: editingTemplate ? 'Updated' : 'Created' });
            closeForm();
        },
        onError: (err: any) => addToast({ type: 'error', title: 'Failed', message: err?.response?.data?.error?.message }),
    });

    const deleteMutation = useMutation({
        mutationFn: (id: string) => api.deleteReceiptTemplate(user!.tenantId, id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['receipt-templates'] });
            setDeleteConfirm(null);
            addToast({ type: 'success', title: 'Deleted' });
        },
    });

    const setDefaultMutation = useMutation({
        mutationFn: ({ id, type }: { id: string; type: string }) => api.setDefaultReceiptTemplate(user!.tenantId, id, type),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['receipt-templates'] });
            addToast({ type: 'success', title: 'Default template updated' });
        },
    });

    const handleEdit = (template: ReceiptTemplate) => {
        setEditingTemplate(template);
        setForm({
            name: template.name, type: template.type,
            headerText: template.headerText || '', footerText: template.footerText || '',
            showLogo: template.showLogo, showTaxDetails: template.showTaxDetails,
            showBarcode: template.showBarcode, fontSize: template.fontSize,
            paperSize: template.paperSize, isDefault: template.isDefault,
        });
        setShowForm(true);
    };

    const closeForm = () => {
        setShowForm(false);
        setEditingTemplate(null);
        setForm({
            name: '', type: 'receipt', headerText: '', footerText: '',
            showLogo: true, showTaxDetails: true, showBarcode: false,
            fontSize: 'normal', paperSize: '80mm', isDefault: false
        });
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center min-h-[60vh]">
                <motion.div animate={{ rotate: 360 }} transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                    className="w-14 h-14 rounded-2xl border-4 border-emerald-200 border-t-emerald-600" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50/30">
            <div className="bg-white/90 backdrop-blur-md border-b border-slate-200 sticky top-0 z-30 shadow-sm">
                <div className="max-w-[1600px] mx-auto px-6 lg:px-8 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <button onClick={() => navigate('/settings')} className="p-2 rounded-xl hover:bg-slate-100 transition-colors" type="button">
                                <ArrowLeft className="w-5 h-5 text-slate-600" />
                            </button>
                            <div className="p-2.5 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl shadow-lg shadow-emerald-500/25">
                                <FileText className="w-5 h-5 text-white" />
                            </div>
                            <div>
                                <h1 className="text-2xl font-bold text-slate-900">Receipt Templates</h1>
                                <p className="text-sm text-slate-500">{templates?.length || 0} template{(templates?.length || 0) !== 1 ? 's' : ''} configured</p>
                            </div>
                        </div>
                        <button onClick={() => { closeForm(); setShowForm(true); }}
                            className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-xl hover:from-emerald-700 transition-all font-semibold shadow-lg text-sm">
                            <Plus className="w-4 h-4" /> Add Template
                        </button>
                    </div>
                </div>
            </div>

            <div className="max-w-[1600px] mx-auto p-6 lg:p-8 space-y-6">
                {(!templates || templates.length === 0) ? (
                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                        className="text-center py-20 bg-white rounded-3xl border border-slate-200">
                        <div className="w-20 h-20 bg-emerald-100 rounded-3xl flex items-center justify-center mx-auto mb-6">
                            <FileText className="w-10 h-10 text-emerald-400" />
                        </div>
                        <h3 className="text-xl font-bold text-slate-700">No templates yet</h3>
                        <p className="text-slate-400 mt-2 max-w-md mx-auto">Create receipt templates for sales receipts, invoices, and kitchen orders.</p>
                        <button onClick={() => { closeForm(); setShowForm(true); }}
                            className="mt-6 px-6 py-3 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-xl font-semibold shadow-lg inline-flex items-center gap-2">
                            <Plus className="w-5 h-5" /> Create Template
                        </button>
                    </motion.div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {(templates || []).map((template, i) => {
                            const meta = TYPE_META[template.type] || TYPE_META.receipt;
                            const Icon = meta.icon;
                            return (
                                <motion.div key={template.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                                    whileHover={{ y: -4 }}
                                    className={clsx(
                                        'group relative bg-white rounded-2xl border-2 transition-all duration-300 overflow-hidden',
                                        template.isDefault ? 'border-emerald-400 shadow-lg shadow-emerald-500/10' : 'border-slate-200 shadow-sm hover:shadow-lg'
                                    )}>
                                    {template.isDefault && <div className="h-1 bg-gradient-to-r from-emerald-500 to-teal-600" />}
                                    <div className="p-5">
                                        <div className="flex items-start justify-between mb-3">
                                            <div className="flex items-center gap-3">
                                                <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${meta.gradient} flex items-center justify-center shadow-md`}>
                                                    <Icon size={20} className="text-white" />
                                                </div>
                                                <div>
                                                    <div className="flex items-center gap-2">
                                                        <h3 className="font-semibold text-slate-900">{template.name}</h3>
                                                        {template.isDefault && <Star size={12} className="text-amber-500 fill-amber-500" />}
                                                    </div>
                                                    <p className="text-xs text-slate-500">{meta.label} · {template.paperSize}</p>
                                                </div>
                                            </div>
                                            <Badge variant={template.isActive ? 'success' : 'danger'} size="sm">
                                                {template.isActive ? 'Active' : 'Inactive'}
                                            </Badge>
                                        </div>
                                        <div className="flex gap-1.5 mb-3">
                                            {template.showLogo && <Badge variant="info" size="sm"><Image size={10} className="mr-1" />Logo</Badge>}
                                            {template.showTaxDetails && <Badge variant="info" size="sm">Tax</Badge>}
                                            {template.showBarcode && <Badge variant="info" size="sm"><Barcode size={10} className="mr-1" />Barcode</Badge>}
                                        </div>
                                        <div className="flex items-center gap-2 pt-3 border-t border-slate-100 opacity-0 group-hover:opacity-100 transition-opacity">
                                            <button onClick={() => handleEdit(template)}
                                                className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium text-slate-600 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors">
                                                <Pencil size={12} /> Edit
                                            </button>
                                            {!template.isDefault && (
                                                <>
                                                    <button onClick={() => setDefaultMutation.mutate({ id: template.id, type: template.type })}
                                                        className="flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium text-amber-600 hover:bg-amber-50 rounded-lg transition-colors">
                                                        <Star size={12} /> Default
                                                    </button>
                                                    <button onClick={() => setDeleteConfirm(template)}
                                                        className="flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium text-rose-500 hover:bg-rose-50 rounded-lg transition-colors">
                                                        <Trash2 size={12} /> Delete
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </motion.div>
                            );
                        })}
                    </div>
                )}

                {/* Create/Edit Modal */}
                <SettingsModal
                    open={showForm}
                    onClose={closeForm}
                    title={editingTemplate ? 'Edit Template' : 'Add Receipt Template'}
                    subtitle="Customize receipt print layout"
                    icon={FileText}
                    footer={
                        <>
                            <Button variant="secondary" onClick={closeForm}>Cancel</Button>
                            <Button leftIcon={<Save size={16} />} onClick={() => saveMutation.mutate(form)}
                                isLoading={saveMutation.isPending} disabled={!form.name}>
                                {editingTemplate ? 'Update' : 'Save'}
                            </Button>
                        </>
                    }
                >
                    <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-3">
                            <Input label="Template Name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
                            <div>
                                <label className="block text-xs font-bold text-slate-600 mb-1.5 uppercase tracking-wider">Type</label>
                                <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value as ReceiptTemplate['type'] })}
                                    className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl text-sm bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20">
                                    <option value="receipt">Sales Receipt</option>
                                    <option value="invoice">Invoice</option>
                                    <option value="kitchen_order">Kitchen Order</option>
                                </select>
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="block text-xs font-bold text-slate-600 mb-1.5 uppercase tracking-wider">Font Size</label>
                                <select value={form.fontSize} onChange={(e) => setForm({ ...form, fontSize: e.target.value })}
                                    className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl text-sm bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20">
                                    <option value="small">Small</option><option value="normal">Normal</option><option value="large">Large</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-slate-600 mb-1.5 uppercase tracking-wider">Paper Size</label>
                                <select value={form.paperSize} onChange={(e) => setForm({ ...form, paperSize: e.target.value })}
                                    className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl text-sm bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20">
                                    <option value="58mm">58mm Thermal</option><option value="80mm">80mm Thermal</option>
                                    <option value="A4">A4</option><option value="letter">Letter</option>
                                </select>
                            </div>
                        </div>
                        <Input label="Header Text" value={form.headerText} onChange={(e) => setForm({ ...form, headerText: e.target.value })} placeholder="Welcome to our store!" />
                        <Input label="Footer Text" value={form.footerText} onChange={(e) => setForm({ ...form, footerText: e.target.value })} placeholder="Thank you for your purchase!" />
                        <div className="space-y-2 p-3 bg-slate-50 rounded-xl">
                            {[
                                { key: 'showLogo', label: 'Show Business Logo' },
                                { key: 'showTaxDetails', label: 'Show Tax Details' },
                                { key: 'showBarcode', label: 'Show Barcode' },
                                { key: 'isDefault', label: 'Set as Default Template' },
                            ].map(({ key, label }) => (
                                <label key={key} className="flex items-center gap-2 cursor-pointer">
                                    <input type="checkbox" checked={(form as any)[key]} onChange={(e) => setForm({ ...form, [key]: e.target.checked })}
                                        className="w-4 h-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500" />
                                    <span className="text-sm font-medium text-slate-700">{label}</span>
                                </label>
                            ))}
                        </div>
                    </div>
                </SettingsModal>

                {/* Delete Confirmation */}
                <AnimatePresence>
                    {deleteConfirm && (
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                            <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }}
                                className="bg-white rounded-3xl shadow-2xl w-full max-w-md p-8">
                                <div className="text-center">
                                    <div className="w-16 h-16 rounded-2xl bg-rose-100 flex items-center justify-center mx-auto mb-4">
                                        <Trash2 className="w-8 h-8 text-rose-500" />
                                    </div>
                                    <h2 className="text-xl font-bold text-slate-900">Delete "{deleteConfirm.name}"?</h2>
                                    <p className="text-sm text-slate-500 mt-2">This template will be permanently removed.</p>
                                </div>
                                <div className="flex gap-3 mt-8">
                                    <button onClick={() => setDeleteConfirm(null)} className="flex-1 px-5 py-3 border-2 border-slate-300 rounded-xl font-medium text-sm">Cancel</button>
                                    <button onClick={() => deleteMutation.mutate(deleteConfirm.id)}
                                        className="flex-1 px-5 py-3 bg-gradient-to-r from-rose-500 to-rose-600 text-white rounded-xl hover:from-rose-600 transition-all font-semibold text-sm shadow-lg shadow-rose-500/25">
                                        {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
                                    </button>
                                </div>
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </div>
    );
}